import RegEx from '../regex';
import DateTime from '../dateTime';
import { FormFieldType } from '../enums';

/**
 * Returns a field from form data.
 * @param {Object} formData The form data to search for the field.
 * @param {String} id The field id.
 * @returns Object
 */
export function getFormDataField(formData, id) {
  let field = null;

  if (id && formData) {
    const { groups } = formData;

    for (let index = 0; index < groups.length; ++index) {
      const group = groups[index];
      const foundField = group.fields?.find(f => f.id === id);

      if (foundField) {
        field = foundField;
        break;
      }
    }
  }

  return field;
}

/**
 * Returns a value that determines whether the provided form data is valid based off of the provided input fields and their values.
 * @param {Object} formData The form data.
 * @param {Array<HTMLElement>} inputFields The array of actual input fields to validate.
 * @returns Boolean
 */
export function isFormValid(formData, inputFields) {
  let isValid = true;
  let hasAtLeastOneInvalidField = false;

  if (formData) {
    const { groups } = formData;

    for (let groupIndex = 0; groupIndex < groups.length; ++groupIndex) {
      const group = groups[groupIndex];
      const { fields } = group;

      for (let fieldIndex = 0; fieldIndex < fields.length; ++fieldIndex) {
        const field = fields[fieldIndex];

        isValid = validateFormDataField(field, inputFields);

        if (!isValid) {
          hasAtLeastOneInvalidField = true;
        }
      }
    }
  } else {
    isValid = false;
  }

  return isValid && !hasAtLeastOneInvalidField;
}

/**
 * Validates a form data field against it's equivalent form input field.
 * @param {Object} field The form data field.
 * @param {Array<HTMLElement>} inputFields The form fields to validate.
 * @returns Boolean
 */
export function validateFormDataField(field, inputFields) {
  let isValid = true;
  let hasAtLeastOneInvalidField = false;
  const { type, isRequired, mustMatch } = field;
  const inputField = inputFields?.find(i => i.id === field.id);
  const { checked, role, value } = inputField ?? {};

  let formGroup = null;

  if (
    type === FormFieldType.checkbox ||
    type === FormFieldType.radio ||
    role === 'combobox' /* FormFieldType.select */
  ) {
    formGroup = inputField?.offsetParent?.offsetParent;
  } else {
    formGroup = inputField?.offsetParent;
  }

  const requiredMessage = formGroup?.querySelector('.form__required-message');
  const invalidMessage = formGroup?.querySelector('.form__invalid-message');

  if (isRequired && (!value || ((type === FormFieldType.checkbox || type === FormFieldType.radio) && !checked))) {
    isValid = false;
    hasAtLeastOneInvalidField = !hasAtLeastOneInvalidField ? true : false;

    requiredMessage?.classList.add('form__required-message--visible');
    invalidMessage?.classList.remove('form__invalid-message--visible');
  } else if (type === FormFieldType.password && mustMatch) {
    const fieldToMatch = inputFields.find(i => i.id === mustMatch);

    if (value !== fieldToMatch?.value) {
      isValid = false;
      hasAtLeastOneInvalidField = !hasAtLeastOneInvalidField ? true : false;

      requiredMessage?.classList.remove('form__required-message--visible');
      invalidMessage?.classList.add('form__invalid-message--visible');
    }
  } else if (
    (type === FormFieldType.date && value && !DateTime.isValid(value, 'MM/dd/yyyy')) ||
    (type === FormFieldType.email && value && !RegEx.email.test(value)) ||
    (type === FormFieldType.phone && value && !RegEx.usPhone.test(value))
  ) {
    isValid = false;
    hasAtLeastOneInvalidField = !hasAtLeastOneInvalidField ? true : false;

    requiredMessage?.classList.remove('form__required-message--visible');
    invalidMessage?.classList.add('form__invalid-message--visible');
  }

  if (isValid && !hasAtLeastOneInvalidField) {
    requiredMessage?.classList.remove('form__required-message--visible');
    invalidMessage?.classList.remove('form__invalid-message--visible');
  }

  return isValid && !hasAtLeastOneInvalidField;
}
